import React from "react";
import { Trophy, Timer, Loader2 } from "lucide-react";
import { UserStatsFicha } from "../types";
import type { ProducaoUndRanking } from "../../../../services/controleProducaoService";

interface RankingItemProps {
  usuario: UserStatsFicha;
  index: number;
}

function chipsProducao(usuario: UserStatsFicha): ProducaoUndRanking[] {
  if (usuario.producaoPorUnd?.length) return usuario.producaoPorUnd;
  if (usuario.producaoKgFormatado && usuario.producaoKgFormatado !== "0") {
    return [
      {
        undNome: "",
        total: 0,
        totalFormatado: usuario.producaoKgFormatado,
      },
    ];
  }
  return [];
}

export const RankingItem: React.FC<RankingItemProps> = ({ usuario, index }) => {
  const isFirst = index === 0;
  const isSecond = index === 1;
  const isThird = index === 2;
  const unidades = chipsProducao(usuario);

  return (
    <div
      className={`relative rounded-lg transition-all duration-300 sm:rounded-2xl ${
        isFirst
          ? "border-2 border-yellow-400/40 bg-gradient-to-r from-yellow-500/30 to-yellow-600/20"
          : isSecond
            ? "border-2 border-neutral-400/30 bg-gradient-to-r from-neutral-400/20 to-neutral-500/10 shadow-lg"
            : isThird
              ? "border-2 border-amber-500/30 bg-gradient-to-r from-amber-600/20 to-amber-700/10 shadow-lg"
              : "border-2 border-white/20 bg-white/10 backdrop-blur-sm"
      }`}
    >
      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-8 sm:py-6 lg:px-10 lg:py-7">
        <div className="flex items-center space-x-4 sm:space-x-6">
          <div
            className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full sm:h-20 sm:w-20 lg:h-24 lg:w-24 ${
              isFirst
                ? "bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg"
                : isSecond
                  ? "bg-gradient-to-br from-neutral-300 to-neutral-500 shadow-lg"
                  : isThird
                    ? "bg-gradient-to-br from-amber-500 to-amber-700 shadow-lg"
                    : "bg-white/20"
            }`}
          >
            {index < 3 ? (
              <Trophy className="h-7 w-7 text-white sm:h-10 sm:w-10 lg:h-12 lg:w-12" />
            ) : (
              <span className="text-xl font-black text-white sm:text-3xl lg:text-4xl">
                #{index + 1}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={`mb-1.5 truncate text-2xl font-black sm:mb-2 sm:text-4xl lg:text-5xl ${
                isFirst ? "text-yellow-300" : "text-white"
              }`}
            >
              {usuario.nome} {usuario.sobrenome}
            </h3>
            <div className="mb-2 flex flex-wrap items-center gap-2 sm:gap-3">
              {unidades.length > 0 ? (
                unidades.map((und) => (
                  <span
                    key={und.undNome || und.totalFormatado}
                    className={`rounded-xl px-3 py-1 font-mono text-lg font-black tabular-nums sm:px-4 sm:py-1.5 sm:text-2xl lg:text-3xl ${
                      und.undNome.toLowerCase() === "kg"
                        ? "bg-orange-400/25 text-orange-200"
                        : "bg-white/15 text-white"
                    }`}
                  >
                    {und.totalFormatado}
                  </span>
                ))
              ) : (
                <span className="rounded-xl bg-white/10 px-3 py-1 text-lg font-semibold text-white/60 sm:text-2xl">
                  0
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex items-center space-x-1.5 rounded-full bg-white/10 px-3 py-1 sm:px-4 sm:py-1.5">
                <Timer className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                <span className="text-base font-semibold sm:text-xl lg:text-2xl">
                  {usuario.itensEncerrados}{" "}
                  {usuario.itensEncerrados === 1 ? "item" : "itens"}
                  {usuario.itensEncerrados > 0
                    ? ` · ${usuario.tempoMedioFormatado}`
                    : ""}
                </span>
              </div>
              {usuario.emProducao && (
                <div className="flex items-center space-x-1.5 rounded-full border border-amber-400/30 bg-amber-500/20 px-3 py-1 sm:px-4 sm:py-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-300 sm:h-5 sm:w-5" />
                  <span className="text-sm font-medium text-amber-200 sm:text-lg lg:text-xl">
                    PRODUZINDO
                  </span>
                </div>
              )}
              {isFirst && usuario.pontuacao > 0 && (
                <div className="flex items-center space-x-1.5 rounded-full border border-yellow-400/30 bg-yellow-500/20 px-3 py-1 sm:px-4 sm:py-1.5">
                  <Trophy className="h-3.5 w-3.5 text-yellow-400 sm:h-5 sm:w-5" />
                  <span className="text-sm font-medium text-yellow-300 sm:text-lg lg:text-xl">
                    LÍDER
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 pt-2 sm:flex-col sm:items-end sm:gap-1 sm:border-0 sm:pt-0 sm:text-right">
          <span className="text-sm opacity-75 sm:hidden">nota</span>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-3xl font-black sm:text-6xl lg:text-7xl ${
                isFirst ? "text-yellow-300" : "text-white"
              }`}
            >
              {usuario.pontuacao.toLocaleString("pt-BR", {
                maximumFractionDigits: 1,
              })}
            </span>
            <span className="text-lg font-semibold opacity-80 sm:text-2xl lg:text-3xl">
              / 10
            </span>
          </div>
          <p className="hidden text-sm font-medium opacity-75 sm:mt-1 sm:block sm:text-lg lg:text-xl">
            produzir mais e mais rápido
          </p>
        </div>
      </div>
    </div>
  );
};
